import React, {
    forwardRef,
    RefObject,
    useContext,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';
import {MaterialContext, Panel, PanelProps} from '@wonderlandengine/react-ui/components';

import {Object3D, Texture} from '@wonderlandengine/api';
import {FlatOpaqueTextured} from '../../types/materials.js';
import {quat} from 'gl-matrix';

export const RotatableImage = forwardRef<
    Object3D,
    React.PropsWithChildren<{src: string | Texture; rotation: number} & PanelProps>
>((props, ref) => {
    const context = useContext(MaterialContext);
    const mat = (props.material ??
        useMemo(() => context.panelMaterialTextured?.clone(), [])) as FlatOpaqueTextured;
    const texture =
        typeof props.src === 'string'
            ? useMemo(() => mat!.engine.textures.load(props.src as string), [props.src])
            : Promise.resolve(props.src);
    texture.then((t) => {
        mat.setFlatTexture(t);
    });

    const panelRef = useRef<Object3D>(null);

    useLayoutEffect(() => {
        if (typeof ref === 'function') {
            ref(panelRef.current);
        } else if (ref) {
            (ref as RefObject<Object3D | null>).current = panelRef.current;
        }
    }, [ref]);

    useLayoutEffect(() => {
        if (panelRef.current) {
            const firstChild = panelRef.current.children[0];
            const tempQuat = quat.create();
            if (firstChild) {
                quat.rotateZ(tempQuat, tempQuat, (props.rotation * Math.PI) / 180);
                firstChild.setRotationLocal(tempQuat);
            }
        }
    }, [props.rotation]);

    return (
        <Panel rounding={0} {...props} material={mat} ref={panelRef}>
            {props.children}
        </Panel>
    );
});

RotatableImage.displayName = 'RotatableImage';
